import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface User {
    email: string;
    userId: string;
    token: string;
}

interface AuthContextType {
    user: User | null;
    logout: () => Promise<void>;
    isLoading: boolean;
    checkUser: () => Promise<void>;
    isAuthModalOpen: boolean;
    openAuthModal: () => void;
    closeAuthModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

    const checkUser = async () => {
        try {
            const { username, userId } = await getCurrentUser();
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (token) {
                setUser({
                    email: username, // Amplify uses username as email often, or we fetch attributes
                    userId: userId,
                    token: token
                });
            }
        } catch (err) {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkUser();

        // Listen for auth events
        const listener = Hub.listen('auth', (data) => {
            const { payload } = data;
            if (payload.event === 'signedIn') {
                checkUser();
            } else if (payload.event === 'signedOut') {
                setUser(null);
            }
        });

        return () => listener();
    }, []);

    const logout = async () => {
        try {
            await signOut();
            setUser(null);
            window.location.href = '/';
        } catch (error) {
            console.error("Error signing out: ", error);
        }
    };

    const openAuthModal = () => {
        setIsAuthModalOpen(true);
    };

    const closeAuthModal = () => {
        setIsAuthModalOpen(false);
    };

    return (
        <AuthContext.Provider value={{ user, logout, isLoading, checkUser, isAuthModalOpen, openAuthModal, closeAuthModal }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
